import { useEffect, useState } from "react";
import type { Location } from "@tabbio/types";
import { api } from "./api";
import { LOCATION_SECTIONS } from "./country-data";
import type { LocationSection } from "./country-data";
import { containsArabicScript } from "./city-names";
import { searchCuratedLocations } from "./curated-location-search";

export type { LocationSection } from "./country-data";

export const CURATED_SECTIONS: LocationSection[] = LOCATION_SECTIONS;

/**
 * Resolve Arabic queries against the same curated canonical values rendered by
 * the picker. The API stores English canonical values, so the Arabic label is
 * only a search alias and never leaks into saved CV/profile data.
 */
// Search always hits the backend so picker behaviour is one source-of-truth
// across native and web. The curated `CURATED_SECTIONS` are used by pickers
// only as the instant empty-query view: never as a substring filter that
// would produce a different ranking from the typed-search path.
export async function searchLocations(
  query: string,
  opts: {
    country?: string;
    limit?: number;
    signal?: AbortSignal;
    includeCountries?: boolean;
  } = {},
): Promise<Location[]> {
  const q = query.trim();
  if (q.length < 2) return [];

  if (containsArabicScript(q)) {
    return searchCuratedLocations(q, opts);
  }

  const params = new URLSearchParams({ q, limit: String(opts.limit ?? 20) });
  if (opts.country) params.set("country", opts.country);
  if (opts.includeCountries) params.set("includeCountries", "true");
  const res = await api.get<Location[]>(
    `/api/locations/search?${params.toString()}`,
    { signal: opts.signal },
  );
  if (res.error) return [];
  return res.data ?? [];
}

export { searchCuratedLocations } from "./curated-location-search";

export function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(id);
  }, [value, delayMs]);
  return debounced;
}
